import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type MyInfoModuleSpecsFragment } from '../../../../api/fragments/MyInfoModuleSpecs';
import {
    DeleteMyInfoSettingDocument,
    type DeleteMyInfoSettingMutation,
    type DeleteMyInfoSettingMutationVariables,
} from '../../../../api/mutations/deleteMyInfoSetting';

export type MyInfoSettingActionsProps = {
    module: MyInfoModuleSpecsFragment;
    myInfoSetting: MyInfoModuleSpecsFragment['myInfoSettings'][number];
};

const MyInfoSettingActions = ({ module, myInfoSetting }: MyInfoSettingActionsProps) => {
    const myInfoModuleId = module.id;
    const myInfoSettingId = myInfoSetting.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['myInfoModuleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('myInfoModuleDetails:myInfoSettings.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('myInfoModuleDetails:myInfoSettings.deleteModal.content'),
            okText: t('myInfoModuleDetails:myInfoSettings.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('myInfoModuleDetails:myInfoSettings.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('myInfoModuleDetails:myInfoSettings.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<DeleteMyInfoSettingMutation, DeleteMyInfoSettingMutationVariables>({
                        mutation: DeleteMyInfoSettingDocument,
                        variables: { myInfoModuleId, id: myInfoSettingId },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('myInfoModuleDetails:myInfoSettings.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [apolloClient, myInfoModuleId, myInfoSettingId, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            <Button
                onClick={() => navigate(`/admin/system/modules/${myInfoModuleId}/myInfo/${myInfoSettingId}`)}
                size="small"
                style={{ padding: 0 }}
                type="link"
            >
                {t('myInfoModuleDetails:myInfoSettingsTable.actions.update')}
            </Button>
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('myInfoModuleDetails:myInfoSettingsTable.actions.delete')}
            </Button>
        </Space>
    );
};

export default MyInfoSettingActions;
